import React, { useEffect, useRef, useState } from "react";
import {
  AcmeCoOperationErrorIcon,
  activeIcon,
  deactiveIcon,
  DeleteIcon,
  EditIcon,
  NewNotificationIcon,
  ThreeDots,
} from "../../assets/icons";
import { useNavigate } from "react-router-dom";
import { routes } from "../../helpers/constants";
import { useDispatch, useSelector } from "react-redux";
import {
  reqToGetStaffList,
  reqToDeleteStaff,
  reqToUpdateStaff,
} from "../../redux/slices.js/staff/staffSlice";
import Modal from "../../components/Modal";
import Loader from "../../components/Loader";

const StaffManagement = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const staffList = useSelector((state) => state.staff.staffList);
  const loader = useSelector((state) => state.staff.loader);

  const [selectedStaff, setSelectedStaff] = useState(null);
  const [activeModal, setActiveModal] = useState(false);
  const [modalType, setModalType] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [openRow, setOpenRow] = useState(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    dispatch(
      reqToGetStaffList({
        data: {},
        onSuccess: (res) => {},
        onFailure: (err) => {
          console.error("Failed to fetch staff list:", err);
        },
      })
    );
  }, [dispatch]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpenRow(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleEdit = (id) => {
    navigate(`/staff/edit-staff/${id}`);
  };

  const handleToggleStatus = (staff) => {
    setSelectedStaff(staff);
    if (staff.status === "active") {
      setModalType("deactivate");
      setShowModal(true);
    } else {
      setModalType("activate");
      setActiveModal(true);
    }
  };

  const handleDelete = (staff) => {
    setSelectedStaff(staff);
    setModalType("delete");
    setShowModal(true);
  };

  const handleDeleteStaff = () => {
    if (!selectedStaff) return;

    dispatch(
      reqToDeleteStaff({
        id: selectedStaff.id,
        onSuccess: () => {
          setShowModal(false);
          setSelectedStaff(null);
          setModalType(null);
          dispatch(
            reqToGetStaffList({
              data: {},
              onSuccess: (res) => {},
              onFailure: (err) => {
                console.error("Failed to refresh staff list:", err);
              },
            })
          );
        },
        onFailure: (err) => {
          console.error("Failed to delete staff:", err);
          setShowModal(false);
          setSelectedStaff(null);
          setModalType(null);
        },
      })
    );
  };

  const handleActivate = () => {
    if (!selectedStaff) return;

    const reqObj = {
      action: "active",
    };

    dispatch(
      reqToUpdateStaff({
        id: selectedStaff.id,
        data: reqObj,
        onSuccess: () => {
          setActiveModal(false);
          setSelectedStaff(null);
          setModalType(null);
          dispatch(
            reqToGetStaffList({
              data: {},
              onSuccess: (res) => {},
              onFailure: (err) => {
                console.error("Failed to refresh staff list:", err);
              },
            })
          );
        },
        onFailure: (err) => {
          console.error("Failed to activate staff:", err);
          setActiveModal(false);
          setSelectedStaff(null);
          setModalType(null);
        },
      })
    );
  };

  const handleDeactivate = () => {
    if (!selectedStaff) return;

    const reqObj = {
      action: "deactive",
    };

    dispatch(
      reqToUpdateStaff({
        id: selectedStaff.id,
        data: reqObj,
        onSuccess: () => {
          setShowModal(false);
          setSelectedStaff(null);
          setModalType(null);
          dispatch(
            reqToGetStaffList({
              data: {},
              onSuccess: (res) => {},
              onFailure: (err) => {
                console.error("Failed to refresh staff list:", err);
              },
            })
          );
        },
        onFailure: (err) => {
          console.error("Failed to deactivate staff:", err);
          setShowModal(false);
          setSelectedStaff(null);
          setModalType(null);
        },
      })
    );
  };

  const dataToRender = staffList?.length > 0 ? staffList : [];

  return (
    <>
      {loader && <Loader />}
      <div className="space-y-6">
        <div className="bg-[#EDF7FF] rounded-xl sm:p-6 p-3">
          <div className="flex flex-col xl:flex-row md:items-start gap-4 md:justify-between">
            <div className="flex items-center gap-2">
              <span className="min-[40px]">{AcmeCoOperationErrorIcon}</span>
              <div>
                <span className="text-[16px] font-semibold mb-2 block">
                  Command Centre Staff
                </span>
                <p className="text-[16px] text-gray-500 text-sm mb-0">
                  Manage staff members who have access to the Command Centre
                </p>
              </div>
            </div>
            <div className="mt-4 md:mt-0 flex gap-2 flex-col sm:flex-row">
              <button
                className="whitespace-nowrap btn-light-blue w-full !gap-2"
                onClick={() => navigate(routes.addStaff)}
              >
                {NewNotificationIcon} Add staff member
              </button>
            </div>
          </div>
        </div>

        <div className="w-full overflow-auto mt-4 rounded-lg max-h-[1000px] common-scroll">
          <table className="min-w-[500px] w-full table-auto">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {dataToRender.length > 0 ? (
                dataToRender.map((item, index) => (
                  <tr key={item.id}>
                    <td>{item.name}</td>
                    <td className="!font-normal">{item.email}</td>
                    <td className="!font-normal">{item?.role?.name}</td>
                    <td>
                      <span
                        className={`px-2 py-1 min-w-[57px] inline-block text-center rounded-full text-body ${
                          item.status === "active"
                            ? "bg-[#F6FFEC] text-[#328708]"
                            : "bg-[#FFECEC] text-[#D93025]"
                        }`}
                      >
                        {item.status}
                      </span>
                    </td>
                    <td className="relative">
                      <button
                        type="button"
                        onClick={() =>
                          setOpenRow(openRow === index ? null : index)
                        }
                        className="inline-flex items-center justify-center gap-x-2 rounded-md px-3 py-1"
                      >
                        {ThreeDots}
                      </button>

                      {openRow === index && (
                        <div
                          ref={dropdownRef}
                          className="absolute dropdown right-2 mt-2 w-56 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 z-50"
                        >
                          <div className="py-1">
                            <div
                              className="block px-4 py-2 dropdown-item cursor-pointer text-sub_span !font-medium hover:bg-[#4285F4] hover:text-[#DAFA7B] flex gap-2 items-center"
                              onClick={() => handleEdit(item.id)}
                            >
                              {EditIcon}
                              Edit
                            </div>
                            <div
                              className="block px-4 py-2 dropdown-item cursor-pointer text-sub_span !font-medium hover:bg-[#4285F4] hover:text-[#DAFA7B] flex gap-2 items-center"
                              onClick={() => handleToggleStatus(item)}
                            >
                              {item.status === "active"
                                ? deactiveIcon
                                : activeIcon}
                              {item.status === "active"
                                ? " Deactivate"
                                : " Activate"}
                            </div>
                            <div
                              className="block px-4 py-2 dropdown-item cursor-pointer text-sub_span !font-medium hover:bg-[#4285F4] hover:text-[#DAFA7B] flex gap-2 items-center"
                              onClick={() => handleDelete(item)}
                            >
                              {DeleteIcon}
                              Delete
                            </div>
                          </div>
                        </div>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="text-center py-4 text-gray-500">
                    No staff members found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {showModal && modalType === "delete" && (
          <Modal
            show={showModal}
            onClose={() => {
              setShowModal(false);
              setModalType(null);
              setSelectedStaff(null);
            }}
            onConfirm={handleDeleteStaff}
            title={`Are you sure you want to delete this staff member?`}
          />
        )}

        {showModal && modalType === "deactivate" && (
          <Modal
            show={showModal}
            onClose={() => {
              setShowModal(false);
              setModalType(null);
              setSelectedStaff(null);
            }}
            onConfirm={handleDeactivate}
            title={`Are you sure you want to deactivate this staff member?`}
          />
        )}

        {activeModal && modalType === "activate" && (
          <Modal
            show={activeModal}
            onClose={() => {
              setActiveModal(false);
              setModalType(null);
              setSelectedStaff(null);
            }}
            onConfirm={handleActivate}
            title={`Are you sure you want to activate this staff member?`}
          />
        )}
      </div>
    </>
  );
};

export default StaffManagement;
